import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Badge primitive — mirrors `.ez-badge` from design/app-tokens.css.
 * Variants come straight from the design tokens (`neutral`, `success`, `warn`,
 * `danger`, `info`, `volt`, `gray`).
 *
 * `dot` prepends a small colored circle that matches the variant color, used
 * for "Live" / "Online" markers and similar.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 px-2 py-[2px] rounded-sm text-xs font-medium leading-[1.4] border',
  {
    variants: {
      variant: {
        neutral: 'bg-surface-3 text-text-2 border-line',
        success: 'bg-success-bg text-success border-success-bd',
        warn: 'bg-warn-bg text-warn border-warn-bd',
        danger: 'bg-danger-bg text-danger border-danger-bd',
        info: 'bg-info-bg text-info border-info-bd',
        volt: 'bg-volt-100 text-volt-700 border-volt-200 dark:bg-[rgba(74,222,128,0.10)] dark:text-volt-400 dark:border-[rgba(74,222,128,0.25)]',
        gray: 'bg-surface-3 text-text-3 border-line',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
);

const dotColors: Record<NonNullable<VariantProps<typeof badgeVariants>['variant']>, string> = {
  neutral: 'bg-text-3',
  success: 'bg-success',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  volt: 'bg-volt',
  gray: 'bg-text-4',
};

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'neutral', dot = false, children, ...props }, ref) => {
    const v = variant ?? 'neutral';
    return (
      <span ref={ref} className={cn(badgeVariants({ variant: v }), className)} {...props}>
        {dot && (
          <span
            aria-hidden="true"
            className={cn('w-1.5 h-1.5 rounded-full', dotColors[v])}
          />
        )}
        {children}
      </span>
    );
  },
);
Badge.displayName = 'Badge';

export { badgeVariants };
